/**
 * /dashboard/posh/compliance — Org-wide POSH compliance roster
 * (HR-admin only).
 *
 * The backend is the boundary: GET /api/v1/posh/training/compliance is
 * RequireRole(company_admin, hr_manager) server-side; this UI is a
 * defence-in-depth mirror.
 *
 * Migration note (2026-06-01, Next.js → Vite, Phase 3d): port of
 * frontend/src/app/(dashboard)/dashboard/posh/compliance/page.tsx.
 *   - "use client" dropped
 *   - next/link → @tanstack/react-router Link
 */

import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  ArrowLeft,
  ClipboardCheck,
  Download,
  Search,
  ShieldX,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import type { Role } from "@/types/auth";
import type {
  POSHComplianceRow,
  POSHComplianceStatus,
} from "@/types/posh";
import { toApiError } from "@/lib/api";
import { is2FARequiredError, useCompliance } from "@/hooks/use-posh";
import { fmtDate, fmtDateTime } from "@/lib/posh-format";
import { toCsv } from "@/lib/csv";
import { PoshModuleShell } from "@/components/shared/posh-module-shell";
import { PageHeader } from "@/components/shared/page-header";
import { StatusBadge } from "@/components/shared/status-badge";
import { PoshFeatureGate } from "@/components/posh/posh-feature-gate";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { toast } from "sonner";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/posh/compliance",
)({
  component: ComplianceRoute,
});

const CAN_VIEW_COMPLIANCE = new Set<Role>([
  "company_admin",
  "hr_manager",
]);

type StatusFilter = "all" | POSHComplianceStatus;

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "All statuses" },
  { value: "not_started", label: "Not started" },
  { value: "in_progress", label: "In progress" },
  { value: "compliant", label: "Compliant" },
];

const STATUS_BADGE: Record<
  POSHComplianceStatus,
  { tone: "neutral" | "progress" | "success"; label: string }
> = {
  not_started: { tone: "neutral", label: "Not started" },
  in_progress: { tone: "progress", label: "In progress" },
  compliant: { tone: "success", label: "Compliant" },
};

const STATUS_RANK: Record<POSHComplianceStatus, number> = {
  not_started: 0,
  in_progress: 1,
  compliant: 2,
};

function NoAccess() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  return (
    <PoshModuleShell>
      <PageHeader
        title="POSH compliance"
        description="Org-wide training & certification status."
      />
      <Card>
        <CardContent className="flex flex-col items-center gap-4 py-16 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent text-accent-foreground">
            <ShieldX className="h-6 w-6" />
          </span>
          <div className="space-y-1">
            <p className="text-base font-medium text-gray-900">
              You don&apos;t have access
            </p>
            <p className="max-w-md text-sm text-muted-foreground">
              Only company admins and HR managers can view the org-wide
              POSH compliance roster. Ask an admin on your team, or head
              back to POSH.
            </p>
          </div>
          <Button asChild variant="outline" className="gap-1.5">
            <Link to="/app/$tenantSlug/posh" params={{ tenantSlug }}>
              <ArrowLeft className="h-4 w-4" />
              Back to POSH
            </Link>
          </Button>
        </CardContent>
      </Card>
    </PoshModuleShell>
  );
}

function StatCard({
  label,
  value,
  tone = "default",
  hint,
}: {
  label: string;
  value: number;
  tone?: "default" | "success" | "muted" | "warning";
  hint?: string;
}) {
  const valueTone: Record<typeof tone, string> = {
    default: "text-primary",
    success: "text-green-700",
    muted: "text-muted-foreground",
    warning: "text-amber-700",
  };
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription className="text-xs font-medium uppercase tracking-wide">
          {label}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <p
          className={`text-3xl font-semibold tabular-nums ${valueTone[tone]}`}
        >
          {value}
        </p>
        {hint && (
          <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
        )}
      </CardContent>
    </Card>
  );
}

function RosterSkeleton() {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <Card key={i}>
            <CardHeader className="pb-2">
              <Skeleton className="h-3 w-20" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-12" />
            </CardContent>
          </Card>
        ))}
      </div>
      <Card>
        <CardContent className="space-y-3 py-6">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function ProgressCell({ row }: { row: POSHComplianceRow }) {
  const total = row.mandatory_total;
  const pct =
    total > 0
      ? Math.min(100, Math.round((row.completed / total) * 100))
      : 0;
  return (
    <div className="min-w-[8rem] space-y-1">
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span className="tabular-nums">
          {row.completed} / {total}
        </span>
        <span className="tabular-nums">{pct}%</span>
      </div>
      <Progress
        value={pct}
        className="h-1.5"
        aria-label={`${row.completed} of ${total} mandatory modules completed`}
      />
    </div>
  );
}

function Roster() {
  const q = useCompliance();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<StatusFilter>("all");

  const allRows = useMemo(() => q.data?.rows ?? [], [q.data]);

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const filtered = allRows.filter((r) => {
      if (status !== "all" && r.status !== status) return false;
      if (!needle) return true;
      return (
        r.name.toLowerCase().includes(needle) ||
        r.email.toLowerCase().includes(needle)
      );
    });
    return [...filtered].sort((a, b) => {
      const d = STATUS_RANK[a.status] - STATUS_RANK[b.status];
      if (d !== 0) return d;
      return a.name.localeCompare(b.name);
    });
  }, [allRows, search, status]);

  if (q.isLoading) return <RosterSkeleton />;

  const apiErr = q.isError ? toApiError(q.error) : null;
  if (
    apiErr &&
    ((q.error as { response?: { status?: number } })?.response?.status ===
      403 ||
      is2FARequiredError(q.error))
  ) {
    return (
      <Alert>
        <AlertTitle>You don&apos;t have access</AlertTitle>
        <AlertDescription>
          Your account can&apos;t view the org-wide POSH compliance
          roster. This is restricted to company admins and HR managers.
        </AlertDescription>
      </Alert>
    );
  }
  if (apiErr) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Could not load compliance</AlertTitle>
        <AlertDescription>{apiErr.message}</AlertDescription>
      </Alert>
    );
  }

  const summary = q.data?.summary;

  function exportCsv() {
    if (rows.length === 0) return;
    const header = [
      "Name",
      "Email",
      "Department",
      "Completed",
      "Mandatory total",
      "Status",
      "Certified until",
      "Last activity",
    ];
    const body = rows.map((r) => [
      r.name,
      r.email,
      r.department ?? "",
      String(r.completed),
      String(r.mandatory_total),
      STATUS_BADGE[r.status].label,
      r.certified_until ? fmtDate(r.certified_until) : "",
      r.last_activity_at ? fmtDateTime(r.last_activity_at) : "",
    ]);
    const blob = new Blob([toCsv([header, ...body])], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `posh-compliance-${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(
      `Exported ${rows.length} ${
        rows.length === 1 ? "employee" : "employees"
      } to CSV.`,
    );
  }

  return (
    <div className="space-y-6">
      {summary && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <StatCard
            label="Total employees"
            value={summary.total_employees}
          />
          <StatCard
            label="Compliant"
            value={summary.compliant}
            tone="success"
          />
          <StatCard
            label="In progress"
            value={summary.in_progress}
          />
          <StatCard
            label="Not started"
            value={summary.not_started}
            tone="muted"
          />
          <StatCard
            label="Cert. expiring ≤30d"
            value={summary.cert_expiring_soon}
            tone="warning"
          />
          <StatCard
            label="Mandatory modules"
            value={summary.mandatory_modules}
            tone="muted"
          />
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <ClipboardCheck className="h-5 w-5 text-primary" />
                Employee roster
              </CardTitle>
              <CardDescription>
                Who has completed mandatory POSH training and whose
                certificate is lapsing. Non-compliant employees are
                listed first.
              </CardDescription>
            </div>
            <Button
              type="button"
              variant="outline"
              className="gap-1.5"
              onClick={exportCsv}
              disabled={rows.length === 0}
            >
              <Download className="h-4 w-4" />
              Export CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1 space-y-1.5">
              <Label htmlFor="roster-search">Search</Label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="roster-search"
                  placeholder="Name or email…"
                  className="pl-9"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-1.5 sm:w-56">
              <Label htmlFor="roster-status">Status</Label>
              <Select
                value={status}
                onValueChange={(v) => setStatus(v as StatusFilter)}
              >
                <SelectTrigger id="roster-status">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_FILTERS.map((f) => (
                    <SelectItem key={f.value} value={f.value}>
                      {f.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {allRows.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              No employees yet. Add your team under People, then they
              appear here as they work through mandatory POSH training.
            </div>
          ) : rows.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              No employees match your search or filter.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead>Progress</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Certified until</TableHead>
                    <TableHead>Last activity</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => {
                    const badge = STATUS_BADGE[r.status];
                    return (
                      <TableRow key={r.user_id}>
                        <TableCell className="font-medium text-gray-900">
                          {r.name}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {r.email}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {r.department?.trim() ? r.department : "—"}
                        </TableCell>
                        <TableCell>
                          <ProgressCell row={r} />
                        </TableCell>
                        <TableCell>
                          <StatusBadge
                            tone={badge.tone}
                            label={badge.label}
                          />
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-muted-foreground">
                          {fmtDate(r.certified_until)}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-muted-foreground">
                          {fmtDateTime(r.last_activity_at)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          {allRows.length > 0 && (
            <p className="text-xs text-muted-foreground">
              Showing {rows.length} of {allRows.length}{" "}
              {allRows.length === 1 ? "employee" : "employees"}.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ComplianceRoute() {
  const { user } = useAuth();
  const role = user?.role;
  const allowed = !!role && CAN_VIEW_COMPLIANCE.has(role);

  if (!allowed) return <NoAccess />;

  return (
    <PoshModuleShell
      title="POSH compliance"
      description="Org-wide POSH training & certification status — the actionable roster for your statutory records. Export it for the auditor."
    >
      <PoshFeatureGate>
        <Roster />
      </PoshFeatureGate>
    </PoshModuleShell>
  );
}
